import type { Filters } from '../components/backlog/FilterBar';
import type { Id } from '../api/types';

/**
 * The URL rules for the backlog's filters, as pure functions over `URLSearchParams`.
 *
 * 🔴 SEPARATE FROM THE HOOK ON PURPOSE. These are the rules a shared link has to obey — which
 *   parameters appear, which are dropped, and what a change to one does to another. Inside
 *   `useCallback` they can only be exercised by rendering a component; out here they are ordinary
 *   functions with assertable answers, and this repo's committed test setup has no DOM renderer.
 */

/** ⭐ `''` deletes rather than writes an empty value: `?assignee=` is not the same URL as no filter. */
export function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
): void {
  if (value === null || value === undefined || value === '') params.delete(key);
  else params.set(key, value);
}

/**
 * The next query string after a filter change.
 *
 * 🔴 A SPRINT DOES NOT SURVIVE A CHANGE OF PROJECT, and this is the rule worth pinning. `sprint.name`
 *   is unique only WITHIN a project (db/001), so sprint 12 of APFS is simply not a thing in PH.
 *   Carried across it would produce `?project=2&sprintId=12` — which the API answers CORRECTLY with
 *   zero rows. ⛔ And zero rows reads as "this project has no tickets", not as "you are still
 *   filtered by another project's sprint". A filter that has become meaningless is dropped rather
 *   than left to produce an empty list nobody can account for.
 *   ⚠️ Note it is keyed on the project CHANGING, not on the project being absent: clearing the
 *     project also clears the sprint, because a sprint with no project is the same orphan.
 *
 * ⭐ Any filter change returns to page 1. Staying on page 7 of a result that now has 2 pages renders
 *   an empty list indistinguishable from "nothing matched".
 *
 * ⚠️ Everything NOT named here is preserved — `ticket`, `expanded` and any parameter a later feature
 *   adds. A filter change is not a reason to close the open ticket or collapse the expanded rows.
 */
export function nextFilterParams(
  current: URLSearchParams,
  nextFilters: Filters,
  previousProjectId: Id | null,
): URLSearchParams {
  const next = new URLSearchParams(current);
  const projectChanged = nextFilters.projectId !== previousProjectId;

  setOrDelete(next, 'project', nextFilters.projectId);
  setOrDelete(next, 'status', nextFilters.status);
  setOrDelete(next, 'assignee', nextFilters.assignee);
  setOrDelete(next, 'sprintId', projectChanged ? null : nextFilters.sprintId);
  next.delete('page');

  return next;
}

/** The filters a URL currently expresses — the exact inverse of what `nextFilterParams` writes. */
export function filtersFromParams(params: URLSearchParams): Filters {
  return {
    projectId: params.get('project'),
    status: params.get('status'),
    assignee: params.get('assignee'),
    sprintId: params.get('sprintId'),
  };
}
